import { ApolloClient } from "apollo-client";
import { AppProps, default as NextApp, DefaultAppIProps, NextAppContext } from "next/app";
import Head from "next/head";
import React from "react";
import { ApolloProvider, getDataFromTree } from "react-apollo";
import { getDisplayName } from "../helpers";
import { getApolloClient } from "./initClient";
import { createSessionToken } from "./createSessionToken";
export interface IProps extends AppProps, DefaultAppIProps {
  apolloState: any;
}

export interface WithApolloState {
  apolloClient: ApolloClient<any>;
}

export const withApolloClient = (App: typeof NextApp) => {
  return class WithApollo extends App<IProps, WithApolloState> {
    public static displayName = `withApollo(${getDisplayName(App)})`;

    public static async getInitialProps(appContext: NextAppContext) {
      const { Component, router, ctx } = appContext;
      let appProps = { pageProps: {} };

      if (App.getInitialProps) {
        appProps = await App.getInitialProps(appContext);
      }

      // Run all GraphQL queries in the component tree
      // and extract the resulting data
      const apollo = getApolloClient({});
      if (!process.browser) {
        // Run all GraphQL queries
        await getDataFromTree(
          <ApolloProvider client={apollo}>
            <App {...appProps} Component={Component} router={router} />
          </ApolloProvider>
        );

        // getDataFromTree does not call componentWillUnmount
        // head side effect therefore need to be cleared manually
        Head.rewind();
      }

      // Extract query data from the Apollo store
      const apolloState = apollo.cache.extract();

      return {
        ...appProps,
        apolloState
      };
    }

    constructor(props: IProps) {
      super(props);
      this.state = {
        apolloClient: getApolloClient({
          initialState: props.apolloState
        })
      };
    }

    async componentDidMount() {
      const { apolloState } = this.props;
      const sessionToken = await createSessionToken();

      const httpLinkConfig = {
        headers: {
          authorization: sessionToken ? `Bearer ${sessionToken}` : ""
        }
      };
      const apolloClient = getApolloClient({
        initialState: apolloState,
        httpLinkConfig
      });

      this.setState({ apolloClient });
    }

    public render() {
      const { apolloClient } = this.state;

      return (
        <ApolloProvider client={apolloClient}>
          <App {...this.props} />
        </ApolloProvider>
      );
    }
  };
};
